import * as React from 'react'
import Grid from '@mui/material/Grid';
import { Box, Button, Divider, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, styled, tableCellClasses } from '@mui/material';
import { usersApi } from '../../api-interface';
import { useLoaderData, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import UnstyledLink from '../../components/common/UnstyledLink/UnstyledLink';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from '@mui/icons-material/Delete';
import { useContext } from 'react';
import { TenantContext } from '../..';
import { useSnackbar } from 'notistack';
import HeaderTableCell from '../../components/common/StyledTableCell/StyledTableCell';
import { UserResp } from '../../api';


export async function loader(tenant_id) {
    const users = await usersApi(tenant_id).getUsersUsersGet()
    const pendingRegistration = await usersApi(tenant_id).getPendingRegistrationsUsersPendingGet()
    const users_ret = users.data;
    const pending_ret = pendingRegistration.data
    return { users: users_ret, pending: pending_ret }
}


const Users = () => {
    const { users, pending } = useLoaderData() as { users: UserResp, pending: any };
    const tenant_id = useContext(TenantContext)
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar();

    async function passwordReset(target_id, tenant_id) {
        console.log(target_id)
        if (window.confirm("Are you sure you want to email a password reset to this user?")) {
            try{
                await usersApi(tenant_id).resetUserPasswordUsersUserIdResetPasswordPost(target_id)
                enqueueSnackbar("Password reset email sent", { variant: "success" })
            } catch(e){
                if (e?.response?.data?.detail){
                    enqueueSnackbar(e.response.data.detail, { variant: "error" })
                } else{
                    enqueueSnackbar("Error: " + e, { variant: "error" })
                }
            }


        }
    }

    async function deleteUser(user_id, tenant_id, navigate) {
        console.log(user_id)
        if (window.confirm("Are you sure you want to remove this user?")) {
            try{
                await usersApi(tenant_id).removeUserFromTenantUsersUserIdDelete(user_id)
                enqueueSnackbar("User deleted", { variant: "success" })
            } catch(e){
                if (e?.response?.data?.detail){
                    enqueueSnackbar(e.response.data.detail, { variant: "error" })
                } else{
                    enqueueSnackbar("Error: " + e, { variant: "error" })
                }
            }


        }
        navigate("")
    }

    async function delete_reg(target_id, tenant_id, navigate) {
        if (window.confirm("Are you sure you want to cancel this users pending registration?")) {
            try{
                await usersApi(tenant_id).deletePendingRegistrationUsersPendingPendingIdDelete(target_id)
                enqueueSnackbar("Registration cancelled", { variant: "success" })
                navigate("")
            } catch(e){
                if (e?.response?.data?.detail){
                    enqueueSnackbar(e.response.data.detail, { variant: "error" })
                } else{
                    enqueueSnackbar("Error: " + e, { variant: "error" })
                }
            }

        }

    }

    async function refresh(target_id, tenant_id, navigate) {
        try{
            await usersApi(tenant_id).refreshPendingRegistrationUsersPendingPendingIdRefreshPost(target_id)
            enqueueSnackbar("Registration email resent", { variant: "success" })
            navigate("")
        } catch(e){
            if (e?.response?.data?.detail){
                enqueueSnackbar(e.response.data.detail, { variant: "error" })
            } else{
                enqueueSnackbar("Error: " + e, { variant: "error" })
            }
        }

    }

    const PendingRow = ({ pending }) => {
        const tenant_id = useContext(TenantContext)
        const navigate = useNavigate()
        const is_expired = new Date() > new Date(pending.expiry)
        const formatted_date = new Date(pending.expiry).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric' })
        const resend = is_expired ? <Tooltip onClick={() => refresh(pending.id, tenant_id, navigate)} title="Resend registration email"><LockResetIcon cursor="pointer" /></Tooltip> : <></>
        return (
            <TableRow
                key={pending.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
                <TableCell>{pending.email}</TableCell>
                <TableCell>{is_expired ? "Expired" : formatted_date}</TableCell>
                <TableCell>
                    {resend}
                    <Tooltip title="Cancel pending registration" onClick={() => delete_reg(pending.id, tenant_id, navigate)}><DeleteIcon cursor="pointer"></DeleteIcon></Tooltip>
                </TableCell>
            </TableRow>
        )
    }

    const PendingRegTable = ({ pending }) => {
        if (pending.length == 0) {
            return <></>
        }
        return (<Grid container size={{xs:12}} sx={{ mt: 10 }} >
            <Typography variant="h5" > Pending Registrations </Typography>
            <TableContainer component={Paper} >
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead >
                        <TableRow>
                            <HeaderTableCell sx={{ backgroundColor: "black", color: "white" }}>Email</HeaderTableCell>
                            <HeaderTableCell>Registration Link Expiry</HeaderTableCell>
                            <HeaderTableCell sx={{ minWidth: 100 }}></HeaderTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pending.map((pending) => (
                            <PendingRow key={pending.id} pending={pending} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>)
    }


    return (
        <Box display="flex" flexDirection={"column"} gap={5}>
            <Box display="flex" flexDirection={"row"} justifyContent={"space-between"}>
                <Typography variant="h4"> User Management </Typography>
                <UnstyledLink to="add">
                    <Button variant="contained">New</Button>
                </UnstyledLink>
            </Box>
            <Box display="flex" flexDirection={"column"} gap={1}>
                <Typography variant="h5"> Registered Users </Typography>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <HeaderTableCell sx={{ backgroundColor: "black", color: "white" }}>First Name</HeaderTableCell>
                                <HeaderTableCell>Last Name</HeaderTableCell>
                                <HeaderTableCell>Email</HeaderTableCell>
                                <HeaderTableCell>User Status</HeaderTableCell>
                                <HeaderTableCell>Reminders</HeaderTableCell>
                                <HeaderTableCell>User Management</HeaderTableCell>
                                <HeaderTableCell sx={{ minWidth: 100 }}></HeaderTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.users.map((user) => (
                                <TableRow
                                    key={user.user_id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>{user.first_name}</TableCell>
                                    <TableCell>{user.last_name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.user_disabled ? "Disabled" : "Enabled"}</TableCell>
                                    <TableCell>{user.receive_reminders ? "Yes" : "No"}</TableCell>
                                    <TableCell>{user.user_management ? "Yes" : "No"}</TableCell>
                                    <TableCell>
                                        <UnstyledLink to={user.user_id}><Tooltip title="Edit user"><EditIcon /></Tooltip></UnstyledLink>
                                        <Tooltip title="Generate password reset for user"><LockResetIcon onClick={() => passwordReset(user.user_id, tenant_id)} cursor="pointer" sx={{ ml: 1 }} /></Tooltip>
                                        <Tooltip title="Delete User"><DeleteIcon onClick={() => deleteUser(user.user_id, tenant_id, navigate)} cursor="pointer" sx={{ ml: 1 }} /></Tooltip>

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <PendingRegTable pending={pending} />
        </Box>
    )
}

export default Users